//TODO : Refactor after ai
/**
 * Dialoge der Datenansicht in Viacid-CI: Modal, Formular für anlegen/ändern
 * aus dem Modell, Beziehungs-Auswahl. Logik wie in Weg A (pages/viacid auf
 * ba-mode), Darstellung mit den eigenen Bausteinen.
 */

import { useEffect, useMemo, useState, type ReactNode } from "react";
import { personaVon, useDatenStore } from "@app/store";
import {
  alsListe,
  ausgehendeRelationen,
  datensaetze,
  heute,
  naechsteKennung,
  neueId,
  objektKlasse,
  relation,
  relationsTitel,
  type Aktion,
  type Datensatz,
  type Feld as FeldDef,
  type ObjektKlasse,
  type Wert,
} from "@shared/demo-data/viacid";
import { ch } from "./lib";
import { Feld, Knopf } from "./ui";

export function Modal({
  titel,
  sub,
  children,
  onClose,
}: {
  titel: string;
  sub?: string;
  children: ReactNode;
  onClose: () => void;
}) {
  useEffect(() => {
    const taste = (e: KeyboardEvent) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", taste);
    return () => window.removeEventListener("keydown", taste);
  }, [onClose]);
  return (
    <div
      className="vc-modal"
      role="dialog"
      aria-modal="true"
      aria-label={titel}
      onClick={onClose}
    >
      <div className="vc-modal__box" onClick={(e) => e.stopPropagation()}>
        <div className="vc-modal__head">
          <div>
            <h2>{titel}</h2>
            {sub && <div className="sub">{sub}</div>}
          </div>
          <button
            type="button"
            className="vc-modal__x"
            onClick={onClose}
            aria-label="Schliessen"
          >
            ×
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}

const TITELFELDER = [
  "name",
  "bezeichnung",
  "titel",
  "objektbezeichnung",
  "anlass",
  "mangelbeschreibung",
  "position",
];
const INPUT_TYP: Partial<Record<FeldDef["typ"], string>> = {
  Ganzzahl: "number",
  Dezimalzahl: "number",
  Datum: "date",
  "Datum und Uhrzeit": "datetime-local",
};

function alsEingabe(wert: Wert | undefined): string {
  return wert == null
    ? ""
    : Array.isArray(wert)
      ? wert.join(", ")
      : String(wert);
}
function ausEingabe(feld: FeldDef, text: string): Wert | undefined {
  if (feld.typ === "Boolean") return text === "true";
  if (text.trim() === "") return undefined;
  if (feld.typ === "Ganzzahl") return Math.round(Number(text));
  if (feld.typ === "Dezimalzahl") return Number(text.replace(",", "."));
  if (feld.mehrfach)
    return text
      .split(",")
      .map((t) => t.trim())
      .filter(Boolean);
  return text;
}

export function SatzFormular({
  klasse,
  aktion,
  satz,
  vorbelegung = {},
  vorwerte = {},
  onClose,
}: {
  klasse: ObjektKlasse;
  aktion: Aktion;
  satz?: Datensatz;
  vorbelegung?: Record<string, string>;
  vorwerte?: Record<string, string>;
  onClose: (neu?: Datensatz) => void;
}) {
  const bestand = useDatenStore((s) => s.bestand);
  const rolle = useDatenStore((s) => s.rolle);
  const anlegen = useDatenStore((s) => s.anlegen);
  const aendern = useDatenStore((s) => s.aendern);
  const felder = klasse.felder.filter(
    (f) => f.key !== klasse.kennungFeld && f.typ !== "Datei"
  );
  const einzel = ausgehendeRelationen(klasse.slug).filter(
    (r) => !r.zuViele && r.zu !== klasse.slug
  );
  const [werte, setWerte] = useState<Record<string, string>>(() =>
    Object.fromEntries(
      felder.map((f) => [
        f.key,
        satz
          ? alsEingabe(satz.werte[f.key])
          : (vorwerte[f.key] ??
            ((f.typ === "Datum" || f.typ === "Datum und Uhrzeit") && f.pflicht
              ? f.typ === "Datum"
                ? heute()
                : new Date().toISOString().slice(0, 16)
              : f.typ === "Boolean"
                ? "false"
                : "")),
      ])
    )
  );
  const [bezuege, setBezuege] = useState<Record<string, string>>(() => {
    const persona = personaVon(rolle);
    return Object.fromEntries(
      einzel.map((r) => {
        const bestehend = satz?.bezuege[r.id];
        const vor =
          vorbelegung[r.id] ??
          (r.zu === "mitarbeiter" && !satz && r.kardinalitaetZu === "1"
            ? persona
            : undefined);
        return [
          r.id,
          (Array.isArray(bestehend) ? bestehend[0] : bestehend) ?? vor ?? "",
        ];
      })
    );
  });
  const [fehler, setFehler] = useState<Record<string, string>>({});
  const vorschlaege = useMemo(
    () =>
      Object.fromEntries(
        felder
          .filter((f) => f.typ === "Auswahl")
          .map((f) => [
            f.key,
            [
              ...new Set(
                datensaetze(bestand, klasse.slug).flatMap((d) => {
                  const w = d.werte[f.key];
                  return Array.isArray(w) ? w : w != null ? [String(w)] : [];
                })
              ),
            ].sort(),
          ])
      ),
    [bestand, felder, klasse.slug]
  );

  const speichern = () => {
    const f: Record<string, string> = {};
    for (const feld of felder)
      if (feld.pflicht && !werte[feld.key]?.trim())
        f[feld.key] = "Pflichtangabe";
    for (const r of einzel)
      if (r.kardinalitaetZu === "1" && !bezuege[r.id])
        f[r.id] = "Pflichtangabe";
    setFehler(f);
    if (Object.keys(f).length) return;
    const neueWerte: Record<string, Wert> = {};
    for (const feld of felder) {
      const w = ausEingabe(feld, werte[feld.key] ?? "");
      if (w !== undefined) neueWerte[feld.key] = w;
    }
    const neueBezuege: Record<string, string | string[]> = {};
    for (const r of einzel)
      if (bezuege[r.id]) neueBezuege[r.id] = bezuege[r.id];
    if (satz) {
      aendern(
        {
          slug: klasse.slug,
          id: satz.id,
          werte: neueWerte,
          bezuege: neueBezuege,
        },
        `${aktion.name} – gespeichert.`
      );
      onClose();
      return;
    }
    const kennung = klasse.kennungFeld
      ? naechsteKennung(bestand, klasse.slug)
      : null;
    if (klasse.kennungFeld && kennung) neueWerte[klasse.kennungFeld] = kennung;
    const titelKey = TITELFELDER.find(
      (k) => typeof neueWerte[k] === "string" && String(neueWerte[k]).trim()
    );
    const neu: Datensatz = {
      id: neueId(bestand, klasse.slug),
      titel: titelKey
        ? String(neueWerte[titelKey])
        : `${klasse.name} ${kennung ?? heute()}`,
      untertitel: null,
      kennung,
      status: aktion.ziel ?? klasse.zustaende[0] ?? null,
      werte: neueWerte,
      bezuege: neueBezuege,
    };
    anlegen(klasse.slug, neu, `${ch(klasse.name)} „${neu.titel}“ angelegt.`);
    onClose(neu);
  };

  return (
    <Modal
      titel={ch(aktion.name)}
      sub={ch(klasse.beschreibung)}
      onClose={() => onClose()}
    >
      <div className="vc-grid c2">
        {felder.map((feld) => (
          <Feld
            key={feld.key}
            label={ch(feld.label) + (feld.pflicht ? " *" : "")}
          >
            {feld.typ === "Boolean" ? (
              <select
                value={werte[feld.key] ?? "false"}
                onChange={(e) =>
                  setWerte((w) => ({ ...w, [feld.key]: e.target.value }))
                }
              >
                <option value="true">Ja</option>
                <option value="false">Nein</option>
              </select>
            ) : (
              <input
                id={`feld-${feld.key}`}
                type={INPUT_TYP[feld.typ] ?? "text"}
                step={feld.typ === "Dezimalzahl" ? "any" : undefined}
                value={werte[feld.key] ?? ""}
                list={
                  vorschlaege[feld.key] ? `vorschlag-${feld.key}` : undefined
                }
                onChange={(e) =>
                  setWerte((w) => ({ ...w, [feld.key]: e.target.value }))
                }
              />
            )}
            {fehler[feld.key] && (
              <div className="vc-field__error">{fehler[feld.key]}</div>
            )}
          </Feld>
        ))}
        {einzel.map((r) => (
          <Feld
            key={r.id}
            label={
              ch(relationsTitel(r, "ausgehend")) +
              (r.kardinalitaetZu === "1" ? " *" : "")
            }
          >
            <select
              value={bezuege[r.id] ?? ""}
              onChange={(e) =>
                setBezuege((b) => ({ ...b, [r.id]: e.target.value }))
              }
            >
              <option value="">—</option>
              {datensaetze(bestand, r.zu).map((d) => (
                <option key={d.id} value={d.id}>
                  {d.kennung ? `${d.kennung} · ${d.titel}` : d.titel}
                </option>
              ))}
            </select>
            {fehler[r.id] && (
              <div className="vc-field__error">{fehler[r.id]}</div>
            )}
          </Feld>
        ))}
      </div>
      {Object.entries(vorschlaege).map(([key, liste]) => (
        <datalist key={key} id={`vorschlag-${key}`}>
          {liste.map((w) => (
            <option key={w} value={w} />
          ))}
        </datalist>
      ))}
      <div className="vc-modal__foot">
        <Knopf onClick={() => onClose()}>Abbrechen</Knopf>
        <Knopf art="primary" onClick={speichern}>
          {satz ? "Speichern" : ch(aktion.name)}
        </Knopf>
      </div>
    </Modal>
  );
}

export function BeziehungDialog({
  klasse,
  aktion,
  satz,
  onClose,
}: {
  klasse: ObjektKlasse;
  aktion: Aktion;
  satz: Datensatz;
  onClose: () => void;
}) {
  const bestand = useDatenStore((s) => s.bestand);
  const aendern = useDatenStore((s) => s.aendern);
  const rel = aktion.relation ? relation(aktion.relation) : undefined;
  const [auswahl, setAuswahl] = useState<string[]>(() =>
    rel ? alsListe(satz.bezuege[rel.id]) : []
  );
  if (!rel) return null;
  const zielKlasse = objektKlasse(rel.zu);
  const waehlen = (id: string) =>
    setAuswahl((alt) =>
      rel.zuViele
        ? alt.includes(id)
          ? alt.filter((x) => x !== id)
          : [...alt, id]
        : [id]
    );
  return (
    <Modal
      titel={ch(aktion.name)}
      sub={
        rel.zuViele
          ? `${ch(zielKlasse?.plural ?? "")} auswählen (mehrere möglich)`
          : `${ch(zielKlasse?.name ?? "")} auswählen`
      }
      onClose={onClose}
    >
      <div className="vc-chips">
        {datensaetze(bestand, rel.zu)
          .filter((d) => d.status !== "Ausgelistet")
          .map((d) => (
            <button
              key={d.id}
              type="button"
              className={`vc-chip ${auswahl.includes(d.id) ? "on" : ""}`}
              onClick={() => waehlen(d.id)}
            >
              {d.titel}
              {d.untertitel ? ` · ${d.untertitel}` : ""}
            </button>
          ))}
      </div>
      <div className="vc-modal__foot">
        <Knopf onClick={onClose}>Abbrechen</Knopf>
        <Knopf
          art="primary"
          onClick={() => {
            aendern(
              {
                slug: klasse.slug,
                id: satz.id,
                bezuege: {
                  [rel.id]: rel.zuViele ? auswahl : (auswahl[0] ?? ""),
                },
              },
              `${aktion.name} – gespeichert.`
            );
            onClose();
          }}
        >
          {ch(aktion.name)}
        </Knopf>
      </div>
    </Modal>
  );
}
